import React, {
    DragEvent,
    FC,
    ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react';

import { CloudUploadOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import type { RcFile } from 'antd/lib/upload/interface';

import { useStores } from '../../hooks';
import {
    captureErrorForSentry,
    validateFilename,
    validateFileExtension,
    validateFileObject,
    validateFileSize,
    validateUploadFilesCount,
} from '../../utils';
import { parseFolderPath } from '../../../stores/UploadFilesStore/helpers';
import type { OSFolder } from '../../../stores/UploadFilesStore/interfaces';
import styles from './TableDropZone.module.scss';

const nameSpace = 'filesTable.dropZone';

const DRAG_TYPE_FILES = 'Files';

let dropFileUidCounter = 0;

interface CollectedFile { file: File; relativePath: string; }

const readDirectoryEntries = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => (
    new Promise((resolve, reject) => {
        const all: FileSystemEntry[] = [];
        const readBatch = (): void => {
            reader.readEntries((batch) => {
                if (batch.length === 0) {
                    resolve(all);
                    return;
                }
                all.push(...batch);
                readBatch(); // some browsers cap a single readEntries call at 100 entries
            }, reject);
        };
        readBatch();
    })
);

const entryToFile = (entry: FileSystemFileEntry): Promise<File> => new Promise((resolve, reject) => {
    entry.file(resolve, reject);
});

const collectEntriesRecursively = async (
    entry: FileSystemEntry,
    pathPrefix: string,
): Promise<CollectedFile[]> => {
    if (entry.isFile) {
        const fileEntry = entry as FileSystemFileEntry;
        const file = await entryToFile(fileEntry);
        return [{ file, relativePath: pathPrefix ? `${pathPrefix}/${entry.name}` : '' }];
    }
    if (entry.isDirectory) {
        const dirEntry = entry as FileSystemDirectoryEntry;
        const childEntries = await readDirectoryEntries(dirEntry.createReader());
        const nextPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
        const nested = await Promise.all(
            childEntries.map((child) => collectEntriesRecursively(child, nextPrefix)),
        );
        return nested.flat();
    }
    return [];
};

const buildRcFile = (file: File, relativePath = ''): RcFile => {
    const rc = file as unknown as RcFile;
    if (!rc.uid) {
        dropFileUidCounter += 1;
        rc.uid = `rc-upload-dropzone-${Date.now()}-${dropFileUidCounter}`;
    }
    Object.defineProperty(rc, 'webkitRelativePath', {
        value: relativePath,
        configurable: true,
        writable: true,
    });
    return rc;
};

const hasFileItems = (event: DragEvent): boolean => {
    const types = event.dataTransfer?.types;
    if (!types) {
        return false;
    }
    // `types` is a DOMStringList in some browsers, so use Array-style indexing.
    for (let i = 0; i < types.length; i += 1) {
        if (types[i] === DRAG_TYPE_FILES) {
            return true;
        }
    }
    return false;
};

interface TableDropZoneProps {
    children: ReactNode;
}

const TableDropZone: FC<TableDropZoneProps> = observer(({ children }) => {
    const { t } = useTranslation();
    const { appStore, filesListStore, uploadFilesStore } = useStores();
    const [isDragOver, setIsDragOver] = useState(false);
    /**
     * `dragenter`/`dragleave` fire for every nested element. We track an enter
     * counter so the overlay does not flicker as the cursor moves between
     * children in the table.
     */
    const dragCounterRef = useRef(0);

    const isUploadAvailable = filesListStore.isMyFiles
        || !!filesListStore.getPossibleUploadFolderId();

    const resetDragState = useCallback(() => {
        dragCounterRef.current = 0;
        setIsDragOver(false);
    }, []);

    const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!isUploadAvailable || !hasFileItems(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        if (!isDragOver) {
            setIsDragOver(true);
        }
    }, [isUploadAvailable, isDragOver]);

    const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!isUploadAvailable || !hasFileItems(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        // Show the copy cursor; setting this explicitly avoids the
        // "not allowed" cursor sometimes shown by browsers when no
        // explicit dropEffect is set.
        const { dataTransfer } = event;
        if (dataTransfer) {
            dataTransfer.dropEffect = 'copy';
        }
    }, [isUploadAvailable]);

    const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!isUploadAvailable || !hasFileItems(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
            setIsDragOver(false);
        }
    }, [isUploadAvailable]);

    const onDrop = useCallback(async (event: DragEvent<HTMLDivElement>) => {
        if (!isUploadAvailable || !hasFileItems(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        resetDragState();

        // Collect references synchronously before any await so the DataTransferItemList
        // remains live (browsers clear it after the event handler yields).
        const items = Array.from(event.dataTransfer?.items ?? []);
        let collected: CollectedFile[] = [];
        if (items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
            const entries = items
                .map((item) => item.webkitGetAsEntry())
                .filter((e): e is FileSystemEntry => !!e);
            const nested = await Promise.all(
                entries.map((entry) => collectEntriesRecursively(entry, '')),
            );
            collected = nested.flat();
        } else {
            // Safari < 11.1 / very old browsers: fall back to flat file list (no folder support).
            collected = Array.from(event.dataTransfer?.files ?? []).map((file) => ({ file, relativePath: '' }));
        }

        if (collected.length === 0) {
            return;
        }

        // Resolve the upload destination at drop time so the user always
        // uploads into whatever location is currently displayed.
        const parentFolderId = filesListStore.getPossibleUploadFolderId();

        // Open the My Files upload popup up-front so the user sees progress.
        appStore.openMyFilesUploadPopup();

        // Pass 1: validate serially, tracking accepted count locally so that
        // validateUploadFilesCount sees running totals even before the
        // InsertionBuffer has flushed the batch to the store.
        let runningCount = uploadFilesStore.allFilesCount;
        const validated: { rcFile: RcFile; folder: OSFolder | null }[] = [];
        for (const c of collected) {
            const rcFile = buildRcFile(c.file, c.relativePath);
            const [parentFolder, pathToRoot] = parseFolderPath(rcFile.webkitRelativePath);
            const folder: OSFolder | null = parentFolder ? { parentFolder, pathToRoot } : null;
            try {
                validateFilename(rcFile.name, 'uploadFiles.messages.error');
                validateFileExtension(rcFile.name, appStore.supportedUploadExtensions);
                validateFileSize(rcFile, uploadFilesStore.uploadLimit);
                validateUploadFilesCount(runningCount);
                // Skip validateFileObject for folder entries — the browser already
                // surfaced a real File handle, so the MIME-sniff check would fail on
                // non-standard types and block legitimate files inside directories.
                if (!folder) {
                    // eslint-disable-next-line no-await-in-loop
                    await validateFileObject(rcFile);
                }
                runningCount += 1;
                validated.push({ rcFile, folder });
            } catch {
                // validators surface user-facing messages; skip this file and continue
            }
        }

        if (validated.length === 0) {
            return;
        }

        // Pass 2: dispatch all appendFile calls in the same microtask so the
        // InsertionBuffer debounces them into one createNewTrees call — otherwise
        // each sequential await triggers a separate debounce flush and produces
        // a duplicate folder row per file (SPX-5931).
        const appendResults = await Promise.allSettled(
            validated.map(({ rcFile, folder }) => (
                uploadFilesStore.appendFile({
                    file: rcFile,
                    myFilesParentFolderId: parentFolderId,
                    folder,
                })
            )),
        );
        const appended = validated.filter((_, i) => appendResults[i].status === 'fulfilled');

        if (appended.length === 0) {
            return;
        }

        // Pass 3: for folder files wait for the BE folder to be created so the
        // specterxFolderId is available as the upload destination in Pass 4.
        const folderFiles = appended.filter(({ folder }) => folder !== null);
        const folderActionResults = await Promise.allSettled(
            folderFiles.map(({ rcFile }) => uploadFilesStore.runCreateFolderActions(rcFile)),
        );
        const failedFolderUids = new Set(
            folderFiles
                .filter((_, i) => folderActionResults[i].status === 'rejected')
                .map(({ rcFile }) => rcFile.uid),
        );

        // Pass 4: trigger upload HTTP requests. Without this step rows sit in the
        // drawer with no network activity (the "infinite loading" bug from iter-2).
        appended
            .filter(({ rcFile }) => !failedFolderUids.has(rcFile.uid))
            .forEach(({ rcFile }) => {
                uploadFilesStore.uploadFile(rcFile).catch((error) => {
                    captureErrorForSentry(error, 'TableDropZone.onDrop.uploadFile');
                });
            });
    }, [
        isUploadAvailable,
        appStore,
        filesListStore,
        uploadFilesStore,
        resetDragState,
    ]);

    return (
        <div
            className={styles['drop-zone']}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-testid="filesTable_dropZone"
        >
            {children}
            {isDragOver && (
                <div className={styles.overlay} aria-hidden="true">
                    <div className={styles['overlay-content']}>
                        <CloudUploadOutlined className={styles.icon} />
                        <div className={styles.title}>{t(`${nameSpace}.title`)}</div>
                        <div className={styles.subtitle}>{t(`${nameSpace}.subtitle`)}</div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default TableDropZone;
